import { rememberCompletionReturn } from "../../utils/profileCompletionReturn";
import { hasMeetingLocation } from "../../utils/meetingLocationSelection";
import MeetingLocationPicker from "../../components/meetingLocation/MeetingLocationPicker";
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  collection,
  query,
  where,
  orderBy,
  doc,
  setDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useCollection } from "react-firebase-hooks/firestore";
import {
  ArrowLeft,
  Paperclip,
  Heart,
  Calendar,
  FileText,
  CalendarCheck,
  Bone,
  SendHorizontal,
  Loader2,
  X,
  ChevronDown,
  Clock,
  AlertCircle,
  Copy,
  Trash2,
  Edit2,
  Ban,
  MapPin,
} from "lucide-react";
import "./MessagesPage.css";

import apiClient from "../../api/apiClient";
import { db } from "../../firebase-config";

import { ENDPOINTS } from "../../api/endPoints";
import tablerBone from "../../assets/tabler_bone.png";
import morningIcon from "../../assets/availability1.png";
import afternoonIcon from "../../assets/availability2.png";
import eveningIcon from "../../assets/availability3.png";
import weekedaysIcon from "../../assets/availability4.png";
import weekenedsIcon from "../../assets/availability5.png";
import vacationIcon from "../../assets/availability6.png";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import userPlaceholder from "../../assets/userPlaceholder.png";

import { format } from "date-fns";
import "./MapDashboardPage.css";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { Box } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useGlobalAlert } from "../../context/AlertContext";
import TopNavBar from "../../components/TopNavBar";
import appIcon from "../../assets/appIcon.svg";
import appIcon2 from "../../assets/socialPawsLogo.svg";
import MobileTopBar from "../../components/MobileTopBar";

// Utility to extract just the first name
const getFirstName = (fullName) => {
  if (!fullName || typeof fullName !== "string") return fullName;
  return fullName.trim().split(" ")[0];
};

const getImagesArray = (imgData) => {
  if (!imgData) return [];
  let arr = [];
  if (Array.isArray(imgData)) {
    arr = imgData;
  } else if (typeof imgData === "string") {
    try {
      const parsed = JSON.parse(imgData);
      if (Array.isArray(parsed)) arr = parsed;
    } catch {
      arr = imgData.trim() !== "" ? [imgData] : [];
    }
  }
  return arr.filter((img) => img !== null && img !== undefined && img !== "");
};

// Formatting date grouping for the message feed
const formatDateGrouping = (date) => {
  if (!date) return "";
  const now = dayjs();
  const msgDay = dayjs(date);

  if (now.isSame(msgDay, "day")) {
    return "Today";
  }
  if (now.subtract(1, "day").isSame(msgDay, "day")) {
    return "Yesterday";
  }
  return msgDay.format("ddd, MMM D");
};

const renderMessageWithLinks = (text) => {
  if (!text || typeof text !== "string") return text;

  // Regex matching http://, https://, and www.
  const urlRegex = /((?:https?:\/\/|www\.)[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, idx) => {
    if (part.match(urlRegex)) {
      let formattedUrl = part;
      if (formattedUrl.startsWith("www.")) {
        formattedUrl = `https://${formattedUrl}`;
      }

      return (
        <a
          key={idx}
          href={formattedUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            window.open(formattedUrl, "_blank", "noopener,noreferrer");
          }}
          style={{
            color: "inherit",
            textDecoration: "underline",
            fontWeight: "600",
            wordBreak: "break-all",
            cursor: "pointer",
          }}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

const getDynamicDateLabel = (date) => {
  if (!date) return "FLEXIBLE";
  const target = dayjs(date);
  const today = dayjs();

  if (target.isSame(today, "day")) return "Today";
  if (target.isSame(today.add(1, "day"), "day")) return "Tomorrow";

  return "DATE";
};

const getDynamicTimeLabel = (date) => {
  if (!date) return "TIME";
  const hour = dayjs(date).hour();

  if (hour >= 0 && hour < 12) return "MORNING";
  if (hour >= 12 && hour < 17) return "AFTERNOON";

  return "EVENING";
};

const MessagesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const storage = getStorage();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileChat, setShowMobileChat] = useState(false);

  // Message Selection & Editing State
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const longPressTimerRef = useRef(null);
  const isLongPressRef = useRef(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const queryParams = new URLSearchParams(location.search);
  const urlPartnerId = queryParams.get("partnerId");

  const partnerIdFromStateRef = React.useRef(
    urlPartnerId || location.state?.partnerId,
  );

  useEffect(() => {
    if (urlPartnerId) {
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (location.state?.partnerId) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [urlPartnerId, location.pathname, location.state?.partnerId, navigate]);

  const { triggerAlert } = useGlobalAlert();
  const { profile } = useSelector((state) => state.user);

  const userRole = useMemo(() => {
    return profile?.user_dog_status === 1 ? "pup_parent" : "pup_pals";
  }, [profile?.user_dog_status]);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const [loadingPartnerData, setLoadingPartnerData] = useState(true);
  const [loadingProfileData, setLoadingProfileData] = useState(true);
  const [user, setUser] = useState(null);
  const [partner, setPartner] = useState(null);
  const [chatPartners, setChatPartners] = useState([]);
  const [activeChat, setActiveChat] = useState(null);

  const [adminBroadcastData, setAdminBroadcastData] = useState(null);

  const [inputText, setInputText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);

  const [failedMessages, setFailedMessages] = useState([]);
  const [pendingUploads, setPendingUploads] = useState([]);

  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const messagesEndRef = React.useRef(null);
  const timeDropdownRef = useRef(null);

  const [isSending, setIsSending] = useState(false);
  const [milestone, setMilestone] = useState(0);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  useEffect(() => {
    if (inputText === "" && textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [inputText]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${profile?.id}`);
        if (res.data?.status && res.data.data) {
          setUser(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching user:", err);
      } finally {
        setLoadingProfileData(false);
      }
    };
    if (profile?.id) fetchUserDetails();
  }, [profile?.id]);

  const myDog = user?.dogs?.[0];
  const userDogStatus = user?.user_dog_status || 0;
  const myName = getFirstName(user?.user_name);
  const myDogName = getFirstName(myDog?.dog_name || "My Dog");

  const myUserImageRaw =
    getImagesArray(user?.user_profile_image)[0] ||
    getImagesArray(user?.profile_image)[0] ||
    getImagesArray(myDog?.profile_image)[0];
  const myDogImageRaw =
    getImagesArray(myDog?.dog_images)[0] || getImagesArray(user?.dog_images)[0];

  const myImage = myUserImageRaw || userPlaceholder;
  const myDogImageFallback = myDogImageRaw || dogPlaceholder;

  const displayAvatar = useMemo(() => {
    const isTeamPaws =
      userDogStatus === 1 || user?.team_paws === 1 || myDog?.team_paws === 1;

    if (isTeamPaws) {
      if (myDogImageRaw && myDogImageRaw.trim() !== "") return myDogImageRaw;
      if (myUserImageRaw && myUserImageRaw.trim() !== "") return myUserImageRaw;
      return dogPlaceholder;
    } else {
      if (myUserImageRaw && myUserImageRaw.trim() !== "") return myUserImageRaw;
      return userPlaceholder;
    }
  }, [userDogStatus, user, myDog, myDogImageRaw, myUserImageRaw]);

  const getUnifiedId = (id1, id2) => {
    return [String(id1), String(id2)].sort().join("_");
  };

  const handleUnifiedAvailability = async () => {
    if (!activeChat?.id) {
      return triggerAlert("Please select a chat partner.", "error");
    }

    const partnerDogStatus = activeChat.userDogStatus || 0;
    const canProceed = milestone >= 2 && userDogStatus !== partnerDogStatus;

    if (!canProceed) {
      return triggerAlert(
        "Availability features are not available for this partner yet.",
        "error",
      );
    }

    setIsCheckingAvailability(true);
    try {
      const res = await apiClient.get(
        `/bookingWeeks/getBookingWeeksByUserId/${profile?.id}`,
      );
      const bookingWeeks = res.data?.data || [];
      const partnerId = activeChat.id;

      const hasPartnerData = bookingWeeks.some(
        (week) =>
          String(week.walker_user_id) === String(partnerId) ||
          String(week.requester_user_id) === String(partnerId),
      );

      if (hasPartnerData) {
        navigate("/availability-request", {
          state: {
            partnerId,
            myDogName: myDogName,
            partnerName: activeChat.name,
          },
        });
      } else {
        navigate("/team-paws-availability", {
          state: {
            partnerId,
            myDogName: myDogName,
            partnerName: activeChat.name,
          },
        });
      }
    } catch (err) {
      console.error("Error checking availability status:", err);
      triggerAlert("Error checking availability status.", err);
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  // Broadcast Listener
  useEffect(() => {
    const docRef = doc(db, "web_chat_list", "global_broadcast");
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const ts = data.last_timestamp?.toDate();
        setAdminBroadcastData({
          id: "admin_social_paws",
          chatDocId: "global_broadcast",
          name: "Social Paws",
          avatar: appIcon2,
          userDogStatus: 0,
          partnerDogImage: "",
          partnerDogName: "",
          displayAvatar: appIcon2,
          displayName: "Social Paws",
          lastMsg: data.last_message,
          walkDate: null,
          last_timestamp: ts || new Date(0),
          last_timestamp_str: ts
            ? ts
                .toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })
                .replace(" ", "")
            : "",
          rawPartnerData: { is_admin: true },
        });
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!profile?.id) return;
    const currentUserId = String(profile.id);
    const q = query(
      collection(db, "web_chat_list"),
      where("users", "array-contains", currentUserId),
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const partnerPromises = snapshot.docs.map(async (docSnap) => {
        const data = docSnap.data();
        const partnerId = data.users.find((id) => id !== currentUserId);

        try {
          const res = await apiClient.get(`/user/details/${partnerId}`);
          const pInfo = res.data?.data;
          const ts = data.last_timestamp?.toDate();

          const pFirstName = getFirstName(
            pInfo?.user_name || `User ${partnerId}`,
          );
          const pDogFirstName = getFirstName(
            pInfo?.dogs?.[0]?.dog_name || "Dog",
          );

          const pIsTeamPaws =
            pInfo?.user_dog_status === 1 ||
            pInfo?.team_paws === 1 ||
            pInfo?.dogs?.[0]?.team_paws === 1;

          const pDogImgRaw =
            getImagesArray(pInfo?.dogs?.[0]?.dog_images)[0] ||
            getImagesArray(pInfo?.dog_images)[0];
          const pUserImgRaw =
            getImagesArray(pInfo?.user_profile_image)[0] ||
            getImagesArray(pInfo?.profile_image)[0] ||
            getImagesArray(pInfo?.dogs?.[0]?.profile_image)[0];

          let partnerDisplayAvatar;
          if (pIsTeamPaws) {
            if (pDogImgRaw && pDogImgRaw.trim() !== "")
              partnerDisplayAvatar = pDogImgRaw;
            else if (pUserImgRaw && pUserImgRaw.trim() !== "")
              partnerDisplayAvatar = pUserImgRaw;
            else partnerDisplayAvatar = dogPlaceholder;
          } else {
            if (pUserImgRaw && pUserImgRaw.trim() !== "")
              partnerDisplayAvatar = pUserImgRaw;
            else partnerDisplayAvatar = userPlaceholder;
          }

          let partnerDisplayName = pFirstName;
          if (pIsTeamPaws && pDogFirstName) {
            partnerDisplayName = pDogFirstName;
          }

          return {
            id: partnerId,
            chatDocId: docSnap.id,
            name: pFirstName,
            avatar: pUserImgRaw || "",
            userDogStatus: pInfo?.user_dog_status || 0,
            partnerDogImage: pDogImgRaw || "",
            partnerDogName: pDogFirstName,
            displayAvatar: partnerDisplayAvatar,
            displayName: partnerDisplayName,
            lastMsg: data.last_message,
            walkDate: data.meeting_date,
            walkLat: data.meeting_lat,
            walkLng: data.meeting_lng,
            meetingLocation: data.meeting_location,
            last_timestamp: ts,
            last_timestamp_str: ts
              ? ts
                  .toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                  .replace(" ", "")
              : "",
            dogId: pInfo?.dogs?.[0]?.id,
            availability: data.availability,
            rawPartnerData: pInfo,
          };
        } catch (err) {
          console.error("Error fetching partner info:", err);
          return {
            id: partnerId,
            chatDocId: docSnap.id,
            name: getFirstName(`User ${partnerId}`),
            displayName: getFirstName(`User ${partnerId}`),
            displayAvatar: userPlaceholder,
            lastMsg: data.last_message,
            last_timestamp: data.last_timestamp?.toDate() || new Date(0),
          };
        }
      });

      const results = await Promise.all(partnerPromises);
      setLoadingPartnerData(false);
      results.sort((a, b) => (b.last_timestamp || 0) - (a.last_timestamp || 0));
      setChatPartners(results);

      if (partnerIdFromStateRef.current) {
        const existing = results.find(
          (p) => String(p.id) === String(partnerIdFromStateRef.current),
        );

        if (existing) {
          setActiveChat(existing);
          localStorage.setItem("activeChatId", existing.id);
          setShowMobileChat(true);
        } else {
          try {
            const res = await apiClient.get(
              `/user/details/${partnerIdFromStateRef.current}`,
            );
            const pInfo = res.data?.data;

            const pFirstName = getFirstName(pInfo?.user_name || "");
            const pDogFirstName = getFirstName(
              pInfo?.dogs?.[0]?.dog_name || "",
            );

            const pIsTeamPaws =
              pInfo?.user_dog_status === 1 ||
              pInfo?.team_paws === 1 ||
              pInfo?.dogs?.[0]?.team_paws === 1;

            const pDogImgRaw =
              getImagesArray(pInfo?.dogs?.[0]?.dog_images)[0] ||
              getImagesArray(pInfo?.dog_images)[0];
            const pUserImgRaw =
              getImagesArray(pInfo?.user_profile_image)[0] ||
              getImagesArray(pInfo?.profile_image)[0] ||
              getImagesArray(pInfo?.dogs?.[0]?.profile_image)[0];

            let partnerDisplayAvatar;
            if (pIsTeamPaws) {
              if (pDogImgRaw && pDogImgRaw.trim() !== "")
                partnerDisplayAvatar = pDogImgRaw;
              else if (pUserImgRaw && pUserImgRaw.trim() !== "")
                partnerDisplayAvatar = pUserImgRaw;
              else partnerDisplayAvatar = dogPlaceholder;
            } else {
              if (pUserImgRaw && pUserImgRaw.trim() !== "")
                partnerDisplayAvatar = pUserImgRaw;
              else partnerDisplayAvatar = userPlaceholder;
            }

            let partnerDisplayName = pFirstName;
            if (pIsTeamPaws && pDogFirstName) {
              partnerDisplayName = pDogFirstName;
            }

            const newChat = {
              id: String(partnerIdFromStateRef.current),
              chatDocId: getUnifiedId(
                profile.id,
                partnerIdFromStateRef.current,
              ),
              name: pFirstName,
              avatar: pUserImgRaw || "",
              userDogStatus: pInfo?.user_dog_status || 0,
              partnerDogImage: pDogImgRaw || "",
              partnerDogName: pDogFirstName,
              displayAvatar: partnerDisplayAvatar,
              displayName: partnerDisplayName,
              isNew: true,
              rawPartnerData: pInfo,
            };

            setActiveChat(newChat);
            localStorage.setItem("activeChatId", newChat.id);
            setShowMobileChat(true);
          } catch (e) {
            console.error("New chat user fetch failed", e);
          }
        }

        partnerIdFromStateRef.current = null;
        navigate(location.pathname, { replace: true, state: {} });
      } else if (results.length > 0 && !activeChat) {
        const savedChatId = localStorage.getItem("activeChatId");

        if (savedChatId) {
          const savedChat = results.find(
            (p) => String(p.id) === String(savedChatId),
          );
          if (savedChat) {
            setActiveChat(savedChat);
            return;
          }
        }
        setActiveChat(results[0]);
      }
    });

    return () => unsubscribe();
  }, [profile?.id, activeChat, location.pathname, navigate]);

  const [meetingAddress, setMeetingAddress] = useState("Meeting Point");

  useEffect(() => {
    if (activeChat?.meetingLocation) {
      setMeetingAddress(activeChat.meetingLocation);
    } else {
      setMeetingAddress("Meeting Point");
    }
  }, [activeChat?.meetingLocation]);

  const hasChatPartners = chatPartners?.length > 0;

  const messagesQuery = useMemo(() => {
    if (!activeChat?.chatDocId) return null;
    return query(
      collection(db, "web_chat_list", activeChat.chatDocId, "messages"),
      orderBy("timestamp", "asc"),
    );
  }, [activeChat?.chatDocId]);

  const [messagesSnapshot, loadingMessages] = useCollection(messagesQuery);

  const allMessages = useMemo(() => {
    let formattedDbMessages = [];

    if (messagesSnapshot && messagesSnapshot.docs) {
      formattedDbMessages = messagesSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((msg) => !msg.deleted_by?.includes(String(profile?.id)))
        .map((msg) => {
          const isPending = !msg.timestamp;
          return {
            ...msg,
            computedDate: isPending ? new Date() : msg.timestamp.toDate(),
            status: isPending ? "sending" : "sent",
          };
        });
    }

    const combined = [...formattedDbMessages, ...failedMessages];

    pendingUploads.forEach((pending) => {
      if (!combined.some((msg) => msg.id === pending.id)) {
        combined.push(pending);
      }
    });

    combined.sort(
      (a, b) => a.computedDate.getTime() - b.computedDate.getTime(),
    );

    return combined;
  }, [messagesSnapshot, failedMessages, pendingUploads, profile?.id]);

  const groupedMessages = useMemo(() => {
    const groups = [];
    (allMessages || []).forEach((msg) => {
      const dateStr = formatDateGrouping(msg.computedDate);
      const lastGroup = groups[groups.length - 1];
      if (!lastGroup || lastGroup.dateStr !== dateStr) {
        groups.push({ dateStr, messages: [msg] });
      } else {
        lastGroup.messages.push(msg);
      }
    });
    return groups;
  }, [allMessages]);

  useEffect(() => {
    if (messagesEndRef.current && selectedMessages.length === 0) {
      messagesEndRef.current.scrollTo({
        top: messagesEndRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [allMessages, selectedMessages.length]);

  const toggleMessageSelection = (msg) => {
    if (msg.sender_id === "admin_social_paws") return;
    setSelectedMessages((prev) => {
      const isSelected = prev.find((m) => m.id === msg.id);
      if (isSelected) return prev.filter((m) => m.id !== msg.id);
      return [...prev, msg];
    });
  };

  const handlePointerDown = (e, msg) => {
    if (e.button !== 0 && e.button !== undefined) return;
    if (msg.sender_id === "admin_social_paws") return;
    isLongPressRef.current = false;

    longPressTimerRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      toggleMessageSelection(msg);
      if (navigator.vibrate) navigator.vibrate(50);
    }, 450);
  };

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleCopyMessages = () => {
    const textToCopy = selectedMessages
      .map((m) => m.message)
      .filter(Boolean)
      .join("\n");
    navigator.clipboard.writeText(textToCopy);
    triggerAlert("Copied to clipboard", "success");
    setSelectedMessages([]);
  };

  const handlePrepareEdit = () => {
    if (selectedMessages.length === 1) {
      const msg = selectedMessages[0];
      setInputText(msg.message || "");
      setEditingMessageId(msg.id);
      setSelectedMessages([]);
      textareaRef.current?.focus();
    }
  };

  const handleDeleteSelected = () => {
    setShowDeleteModal(true);
  };

  const [deleteMeLoading, setDeleteMeLoading] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);

  const executeDelete = async (type) => {
    if (
      !activeChat ||
      selectedMessages.length === 0 ||
      deleteMeLoading ||
      deleteAllLoading
    )
      return;

    const chatDocId = activeChat.chatDocId;

    try {
      for (const msg of selectedMessages) {
        const msgRef = doc(db, "web_chat_list", chatDocId, "messages", msg.id);
        if (type === "everyone") {
          setDeleteAllLoading(true);
          await updateDoc(msgRef, {
            is_deleted_for_everyone: true,
            message: "",
            file: null,
          });

          if (allMessages[allMessages.length - 1]?.id === msg.id) {
            await setDoc(
              doc(db, "web_chat_list", chatDocId),
              { last_message: "This message was deleted" },
              { merge: true },
            ).catch(console.error);
          }
        } else if (type === "me") {
          setDeleteMeLoading(true);
          await updateDoc(msgRef, {
            deleted_by: arrayUnion(String(profile.id)),
          });
        }
      }
    } catch (error) {
      console.error("Error deleting messages", error);
      triggerAlert("Failed to delete messages", "error");
    } finally {
      setDeleteMeLoading(false);
      setDeleteAllLoading(false);
      setSelectedMessages([]);
      setShowDeleteModal(false);
    }
  };

  const canCopyMessages =
    selectedMessages.length > 0 &&
    selectedMessages.every((msg) => !msg.is_deleted_for_everyone);

  const canDeleteForEveryone =
    selectedMessages.length > 0 &&
    selectedMessages.every(
      (msg) =>
        msg.sender_id === String(profile?.id) && !msg.is_deleted_for_everyone,
    );

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check File size (50 MB limit)
    const MAX_FILE_SIZE = 50 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      triggerAlert("File is too large. Maximum size allowed is 50MB.", "error");
      return;
    }

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    const fileData = {
      file,
      name: file.name,
      type: file.type,
      size: file.size,
      isImage,
      isVideo,
      previewUrl: isImage || isVideo ? URL.createObjectURL(file) : null,
    };
    setSelectedFile(fileData);
  };

  const handleSend = async () => {
    if ((!inputText.trim() && !selectedFile) || !activeChat || isSending)
      return;

    if (!checkCompletionStatus()) return;

    const textToSend = inputText;
    const fileToSend = selectedFile;
    const isEditing = !!editingMessageId;

    const currentUserId = String(profile.id);
    const partnerId = String(activeChat.id);
    const chatDocId =
      activeChat.chatDocId || getUnifiedId(currentUserId, partnerId);

    const chatRef = doc(db, "web_chat_list", chatDocId);
    const messagesRef = collection(chatRef, "messages");

    setInputText("");
    setSelectedFile(null);
    setEditingMessageId(null);
    setIsSending(true);

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    // Scroll to bottom immediately
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
      }
    }, 50);

    try {
      if (isEditing) {
        const msgRef = doc(messagesRef, editingMessageId);
        await updateDoc(msgRef, {
          message: textToSend,
          is_edited: true,
          edited_at: serverTimestamp(),
        });

        if (allMessages[allMessages.length - 1]?.id === editingMessageId) {
          await setDoc(
            chatRef,
            { last_message: textToSend },
            { merge: true },
          ).catch((e) => console.error("Sidebar Sync Error:", e));
        }
        setIsSending(false);
        return;
      }

      const newMsgRef = doc(messagesRef);
      const newMsgId = newMsgRef.id;

      // Optimistically Add to UI so User sees Media + Text immediately
      const optimisticMsg = {
        id: newMsgId,
        sender_id: currentUserId,
        receiver_id: partnerId,
        message: textToSend || "",
        file: fileToSend
          ? {
              name: fileToSend.name,
              type: fileToSend.type,
              url: fileToSend.previewUrl,
              isImage: fileToSend.isImage,
              isVideo: fileToSend.isVideo,
            }
          : null,
        computedDate: new Date(),
        status: "uploading",
        is_uploading: true,
      };

      setPendingUploads((prev) => [...prev, optimisticMsg]);

      // Scroll to bottom immediately again
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollTop =
            messagesEndRef.current.scrollHeight;
        }
      }, 50);

      // Handle Firebase Upload and saving Document
      let filePayload = null;
      if (fileToSend) {
        const mediaRefPath = fileToSend.isImage
          ? `chat_images/${currentUserId}/${Date.now()}_${fileToSend.name}`
          : `chat_media/${currentUserId}/${Date.now()}_${fileToSend.name}`;

        const storageRef = ref(storage, mediaRefPath);
        const uploadTask = await uploadBytes(storageRef, fileToSend.file);
        const downloadURL = await getDownloadURL(uploadTask.ref);

        filePayload = {
          name: fileToSend.name,
          type: fileToSend.type,
          url: downloadURL,
          isImage: fileToSend.isImage,
          isVideo: fileToSend.isVideo,
        };
      }

      const messagePayload = {
        sender_id: currentUserId,
        receiver_id: partnerId,
        message: textToSend || "",
        file: filePayload || null,
        timestamp: serverTimestamp(),
        is_read: false,
      };

      // Set sidebar
      setDoc(
        chatRef,
        {
          users: [currentUserId, partnerId],
          last_message:
            textToSend ||
            (fileToSend?.isImage
              ? "📷 Image"
              : fileToSend?.isVideo
                ? "🎥 Video"
                : `📎 ${fileToSend?.name}`),
          last_timestamp: serverTimestamp(),
        },
        { merge: true },
      ).catch((e) => console.error("Chat Ref Error:", e));

      // Create Firestore collection doc with our pre-generated ID
      await setDoc(newMsgRef, messagePayload);

      // Remove optimistic from UI when complete
      setPendingUploads((prev) => prev.filter((msg) => msg.id !== newMsgId));

      const messageSummary =
        textToSend ||
        (fileToSend?.isImage
          ? "📷 Image"
          : fileToSend?.isVideo
            ? "🎥 Video"
            : `📎 ${fileToSend?.name}`) ||
        "New message";
      triggerNotificationLogic(currentUserId, partnerId, messageSummary);
      localStorage.setItem("activeChatId", activeChat.id);

      // Clean up object URL to prevent memory leaks AFTER successful upload
      if (fileToSend?.previewUrl) {
        URL.revokeObjectURL(fileToSend.previewUrl);
      }
    } catch (error) {
      console.error("General wrapper send error:", error);
      setPendingUploads((prev) =>
        prev.filter((msg) => msg.id !== editingMessageId),
      );

      setFailedMessages((prev) => [
        ...prev,
        {
          sender_id: currentUserId,
          receiver_id: partnerId,
          message: textToSend || "",
          file: fileToSend
            ? {
                name: fileToSend.name,
                url: fileToSend.previewUrl,
                isImage: fileToSend.isImage,
                isVideo: fileToSend.isVideo,
              }
            : null,
          id: `failed_${Date.now()}`,
          computedDate: new Date(),
          status: "failed",
        },
      ]);
      triggerAlert("Failed to send message. Please try again.", "error");
    } finally {
      setIsSending(false);
    }
  };

  const triggerNotificationLogic = async (
    currentUserId,
    partnerId,
    sentMessageStr,
  ) => {
    try {
      const sessionKey = `notified_partners_${currentUserId}`;
      let notifiedPartners = [];
      const storedItems = sessionStorage.getItem(sessionKey);

      if (storedItems) {
        notifiedPartners = JSON.parse(storedItems);
      }

      if (!notifiedPartners.includes(partnerId)) {
        const partnerEmail =
          activeChat.rawPartnerData?.email ||
          activeChat.rawPartnerData?.user_email;

        if (partnerEmail) {
          await apiClient.post("/receivedMessage/send-received-message", {
            to: partnerEmail,
            dynamicData: {
              first_name: getFirstName(activeChat.name) || "Dear",
              last_messages: [
                {
                  sender: myName || "User",
                  message: sentMessageStr,
                },
              ],
            },
          });
          notifiedPartners.push(partnerId);
          sessionStorage.setItem(sessionKey, JSON.stringify(notifiedPartners));
        }
      }
    } catch (notifyError) {
      console.error("Error triggering send-received-message API:", notifyError);
    }
  };

  const sidebarList = useMemo(() => {
    let list = [...chatPartners];
    if (adminBroadcastData) {
      list.push(adminBroadcastData);
    }
    if (activeChat?.isNew && !list.find((p) => p.id === activeChat.id)) {
      list.push(activeChat);
    }
    list.sort((a, b) => (b.last_timestamp || 0) - (a.last_timestamp || 0));
    return list;
  }, [chatPartners, activeChat, adminBroadcastData]);

  useEffect(() => {
    if (!activeChat || !profile?.id || activeChat.id === "admin_social_paws")
      return;

    const fetchMilestones = async () => {
      try {
        const payload = {
          requester_user_id: activeChat?.id,
          walker_user_id: profile?.id,
        };

        const res = await apiClient.post(
          "/milestone/get-requester-and-walker-milestones",
          payload,
        );

        if (res.data?.status && res.data.data) {
          const { requester, walker } = res.data.data;
          const partnerData =
            requester.user_id !== profile.id ? requester : walker;
          setMilestone(partnerData.milestone || 0);
        }
      } catch (err) {
        console.error("Error fetching milestones:", err);
      }
    };

    fetchMilestones();
  }, [activeChat?.id, profile?.id]);

  const [isTossingTreat, setIsTossingTreat] = useState(false);
  const [isCreatingWalk, setIsCreatingWalk] = useState(false);
  const [showTreatModal, setShowTreatModal] = useState(false);
  const [treatDog, setTreatDog] = useState(null);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedFlexibleOptions, setSelectedFlexibleOptions] = useState([]);
  const [showWalkConfirmModal, setShowWalkConfirmModal] = useState(false);
  const [showMeetingPointModal, setShowMeetingPointModal] = useState(false);
  const [showBookWalkModal, setShowBookWalkModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [walkTab, setWalkTab] = useState("calendar");
  const [walkMessage, setWalkMessage] = useState("");

  const [selectedTime, setSelectedTime] = useState(null);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [value, setValue] = useState(dayjs());

  const [selectedMeetingAddress, setSelectedMeetingAddress] = useState("");
  const [selectedMeetingLocation, setSelectedMeetingLocation] = useState(null);
  const [userLocation] = useState({
    lat: 40.7648,
    lng: -73.9808,
  });

  const handleCloseModal = () => {
    setShowTreatModal(false);
    setShowBookWalkModal(false);
    setTreatDog(null);
    setValue(dayjs());
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const [showIncompleteProfileModal, setShowIncompleteProfileModal] =
    useState(false);

  const checkCompletionStatus = () => {
    const isComplete = profile?.dogs?.[0]?.isDogProfileComplete;
    const isHalfComplete = profile?.dogs?.[0]?.isDogProfileHalfComplete;

    if (isComplete === 1) {
      return true;
    }
    if (isComplete === 0 && isHalfComplete === 1) {
      rememberCompletionReturn(profile?.id, { pId: activeChat?.id, activeView: "dog" });
      navigate("/half-completed-page");
      return false;
    }
    setShowIncompleteProfileModal(true);
    return false;
  };

  const handleTossTreat = async (dogId) => {
    if (!checkCompletionStatus()) return;

    if (!profile?.id || !activeChat?.id) {
      triggerAlert("User or Dog information missing", "error");
      return;
    }

    setIsTossingTreat(true);

    try {
      const payload = {
        sender_user_id: profile?.id,
        receiver_user_id: activeChat?.id,
        receiver_dog_id: activeChat?.dogId,
      };

      const response = await apiClient.post(
        ENDPOINTS.CREATE_WEBAPP_TREAT,
        payload,
      );

      if (response.data?.status) {
        setTreatDog({
          dog_name: activeChat.partnerDogName,
          user_name: activeChat.name,
          dog_images: [activeChat.partnerDogImage],
          profile_image: [activeChat.avatar],
          team_social: activeChat.userDogStatus === 1 ? 0 : 1,
          displayAvatar: activeChat.displayAvatar,
          displayName: activeChat.displayName,
        });
        setShowTreatModal(true);
      } else {
        triggerAlert(
          response.data?.message || "Failed to toss a treat.",
          "error",
        );
      }
    } catch (error) {
      console.error("Error tossing treat:", error);

      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          `Server Error: ${error.response.status}`;
        triggerAlert(errorMessage, "error");
      } else if (error.request) {
        triggerAlert(
          "No response from server. Please check your internet connection.",
          "error",
        );
      } else {
        triggerAlert("An unexpected error occurred.", "error");
      }
    } finally {
      setIsTossingTreat(false);
    }
  };

  const handleCreateWalk = async () => {
    if (!profile?.id || !activeChat?.id) {
      triggerAlert("Missing user or dog profile information.", "error");
      return;
    }

    if (!hasMeetingLocation(selectedMeetingAddress)) {
      triggerAlert("Please select a meeting location.", "error");
      return;
    }

    setIsCreatingWalk(true);

    try {
      const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const effectiveDogId =
        activeChat.userDogStatus === 0
          ? profile?.dogs?.[0]?.id
          : activeChat?.dogId;

      const payload = {
        requester_user_id: profile.id,
        walker_user_id: activeChat.id,
        dog_id: effectiveDogId,
        date: selectedDate ? selectedDate.toISOString() : null,
        availability: walkTab === "flexible" ? selectedFlexibleOptions : [],
        meeting_location: selectedMeetingAddress.trim(),
        timezone: deviceTimezone,
        sender_offer_message: walkMessage,
        receiver_offer_message: "",
      };

      const response = await apiClient.post(ENDPOINTS.CREATE_A_WALK, payload);

      if (response.data?.status) {
        triggerAlert("Walk request sent successfully!", "success");

        if (walkMessage && walkMessage.trim() !== "") {
          try {
            const currentUserId = String(profile.id);
            const partnerId = String(activeChat.id);
            const chatDocId =
              activeChat.chatDocId || getUnifiedId(currentUserId, partnerId);
            const chatRef = doc(db, "web_chat_list", chatDocId);
            const messagesRef = collection(chatRef, "messages");
            const newMsgRef = doc(messagesRef);

            await setDoc(
              chatRef,
              {
                users: [currentUserId, partnerId],
                last_message: walkMessage,
                last_timestamp: serverTimestamp(),
              },
              { merge: true },
            );

            await setDoc(newMsgRef, {
              sender_id: currentUserId,
              receiver_id: partnerId,
              message: walkMessage,
              file: null,
              timestamp: serverTimestamp(),
              is_read: false,
            });

            triggerNotificationLogic(currentUserId, partnerId, walkMessage);
          } catch (chatErr) {
            console.error("Failed to add walk message to chat:", chatErr);
          }
        }

        closeAllModalsFully();
      } else {
        triggerAlert(
          response.data?.message || "Failed to create walk request.",
          "error",
        );
      }
    } catch (error) {
      console.error("Create Walk Error:", error);
      triggerAlert("Something went wrong. Please try again.", "error");
    } finally {
      setIsCreatingWalk(false);
    }
  };

  const TIME_SLOTS = useMemo(() => {
    const interval = 30;
    const slots = [];
    const now = dayjs();
    const isToday = value && value.isSame(now, "day");

    const currentMinute = now.minute();
    const addMinutes = interval - (currentMinute % interval);
    const nextAvailableTime = now
      .clone()
      .add(addMinutes, "minute")
      .second(0)
      .millisecond(0);

    for (let h = 6; h <= 21; h++) {
      for (let m = 0; m < 60; m += interval) {
        if (h === 21 && m > 0) break;

        const slotTime = (value || now)
          .clone()
          .hour(h)
          .minute(m)
          .second(0)
          .millisecond(0);

        if (isToday) {
          if (
            slotTime.isSame(nextAvailableTime) ||
            slotTime.isAfter(nextAvailableTime)
          ) {
            slots.push(slotTime.format("hh:mm A"));
          }
        } else {
          slots.push(slotTime.format("hh:mm A"));
        }
      }
    }

    return slots;
  }, [value]);

  const handleBookWalk = () => {
    if (!checkCompletionStatus()) return;

    setTreatDog({
      dog_name: activeChat.partnerDogName,
      user_name: activeChat.name,
      dog_images: [activeChat.partnerDogImage],
      profile_image: [activeChat.avatar],
      team_social: activeChat.userDogStatus === 1 ? 0 : 1,
      displayAvatar: activeChat.displayAvatar,
      displayName: activeChat.displayName,
    });
    setShowBookWalkModal(true);
  };

  const toggleFlexibleOption = (option) => {
    if (selectedFlexibleOptions.includes(option)) {
      setSelectedFlexibleOptions((prev) => prev.filter((o) => o !== option));
    } else {
      setSelectedFlexibleOptions((prev) => [...prev, option]);
    }
  };

  const closeWalkConfirmAndBackToCalendar = () => {
    setShowWalkConfirmModal(false);
    setShowBookWalkModal(true);
    setWalkTab("calendar");
  };

  const openMeetingPoint = () => {
    setShowBookWalkModal(false);
    setShowWalkConfirmModal(false);
    setShowMeetingPointModal(true);
  };

  const handleFlexibleSubmit = () => {
    if (selectedFlexibleOptions.length === 0) return;
    setSelectedDate(null);
    setSelectedTime(null);
    openMeetingPoint();
  };

  const openWalkConfirm = () => {
    if (!hasMeetingLocation(selectedMeetingAddress)) {
      triggerAlert("Please select a meeting location.", "error");
      return;
    }
    setShowMeetingPointModal(false);
    setShowWalkConfirmModal(true);
  };

  const backToBookWalk = () => {
    setShowMeetingPointModal(false);
    setShowBookWalkModal(true);
  };

  const backToMeetingPoint = () => {
    setShowWalkConfirmModal(false);
    setShowMeetingPointModal(true);
  };

  const closeAllModalsFully = () => {
    setShowTreatModal(false);
    setShowBookWalkModal(false);
    setShowWalkConfirmModal(false);
    setShowMeetingPointModal(false);
    setShowSuccessModal(false);
    setTreatDog(null);
    setSelectedMeetingAddress("");
    setSelectedMeetingLocation(null);
    setValue(dayjs());
    setSelectedDate(null);
    setSelectedTime(null);
    setWalkMessage("");
  };

  const closeAllModals = () => {
    setShowTreatModal(false);
    setShowBookWalkModal(false);
    setShowWalkConfirmModal(false);
    setShowSuccessModal(false);
    setTreatDog(null);
    setValue(dayjs());
    setSelectedDate(null);
    setSelectedTime(null);
    setWalkMessage("");
  };

  const handleGoToAvailabilityPage2 = () => {
    if (!hasChatPartners)
      return triggerAlert(
        "Please complete a booking to view availability.",
        "error",
      );

    navigate("/team-social-availability", {
      state: {
        partnerId: activeChat?.id,
        partnerName: activeChat.name,
      },
    });
  };

  const handleGoToPupCarePage = () => {
    navigate("/walkies-and-food", {
      state: {
        partnerId: userDogStatus === 1 ? profile?.id : activeChat?.id,
      },
    });
  };

  const handleGoToCurrentBookingsPage = () => navigate("/current-bookings");

  const handleImgError = (e) => {
    e.target.src = dogPlaceholder;
  };

  const handleAvatarClick = (e, partner, activeView) => {
    e.stopPropagation();
    if (!partner.rawPartnerData || partner.id === "admin_social_paws") return;

    navigate(`/profile-details`, {
      state: { activeView: activeView, pId: partner.id },
    });
  };

  const handleScrollToTime = () => {
    const isMobile =
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
      (window.matchMedia && window.matchMedia("(max-width: 768px)").matches);

    if (!isMobile) return;

    setShowTimeDropdown(true);
    setTimeout(() => {
      timeDropdownRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
  };

  const handleCalendarClick = (e) => {
    if (e.target.closest(".MuiPickersDay-root")) {
      handleScrollToTime();
    }
  };

  if (loadingPartnerData || loadingProfileData)
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(139.27deg, #009fd9 23.13%, #005b7d 98.96%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
      </div>
    );

  return (
    <div className="msg-container">
      {/* DESKTOP Top Navigation Panel */}
      <div
        className="msg-custom-top-header msg-desktop-only"
        style={{ padding: "2rem" }}
      >
        <div
          className="msg-ui-navbar-div"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            cursor: "pointer",
            marginTop: "-6px",
            marginLeft: "2px",
            flexShrink: "0",
          }}
          onClick={() => navigate("/dashboard-switcher")}
        >
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "28px", height: "28px", objectFit: "contain" }}
          />
          <span
            style={{
              color: "#fff",
              fontSize: "22px",
              fontWeight: "700",
              whiteSpace: "nowrap",
            }}
          >
            Social Paws
          </span>
        </div>

        <div
          className="msg-ui-nav-icons"
          style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
        >
          <TopNavBar theme="dark" />
        </div>
      </div>

      {/* MOBILE Top Navigation Panel */}
      <div className="msg-mobile-header msg-mobile-only">
        <MobileTopBar
          onBack={() => {
            if (isMobile && showMobileChat) {
              setShowMobileChat(false);
            } else {
              navigate("/dashboard-switcher");
            }
          }}
          backColor="#fff"
          logoColor="#fff"
        />

        <div className="msg-mobile-header-bottom">
          <div className="msg-ui-nav-icons" style={{ width: "100%" }}>
            <TopNavBar theme="dark" />
          </div>
        </div>
      </div>

      <div className="msg-content-wrapper">
        {/* LEFT SIDEBAR: Partner List */}
        <div
          className={`msg-sidebar ${isMobile && showMobileChat ? "mobile-hidden" : ""}`}
        >
          {isMobile && <h1 className="mobile-msg-title">Messages</h1>}

          {!loadingPartnerData && sidebarList.length === 0 && (
            <div className="msg-empty-state" style={{ marginTop: "2rem" }}>
              <h3>No messages yet</h3>
              <p style={{ padding: "0 1rem" }}>
                Your conversations will appear here once you connect with
                someone.
              </p>
            </div>
          )}

          {sidebarList.map((partner) => (
            <div
              key={partner.id}
              className={`msg-list-item ${activeChat?.id === partner.id ? "active" : ""}`}
              onClick={() => {
                setActiveChat(partner);
                localStorage.setItem("activeChatId", partner.id);
                setShowMobileChat(true);
              }}
            >
              <div
                className="msg-avatar-container"
                onClick={(e) => handleAvatarClick(e, partner, "user")}
                style={{
                  cursor:
                    partner.id === "admin_social_paws" ? "default" : "pointer",
                }}
              >
                <img
                  src={partner.displayAvatar || userPlaceholder}
                  alt={partner.displayName}
                  onError={handleImgError}
                  style={{
                    padding: partner.id === "admin_social_paws" ? "5px" : "0",
                    backgroundColor:
                      partner.id === "admin_social_paws"
                        ? "#e3f2fd"
                        : "transparent",
                  }}
                />
              </div>
              <div className="msg-chat-info">
                <div className="msg-chat-header-left">
                  <span className="msg-name">{partner.displayName}</span>
                  <p className="msg-preview">{partner.last_timestamp_str}</p>
                </div>
                <p className="msg-preview">{partner.lastMsg}</p>
              </div>
            </div>
          ))}
          <div className="msg-sidebar-filler"></div>
        </div>

        {/* RIGHT PANEL: Active Conversation */}
        <div
          className={`msg-chat-panel ${isMobile && !showMobileChat ? "mobile-hidden" : ""}`}
        >
          {selectedMessages.length > 0 ? (
            <div
              className="msg-chat-header selection-mode"
              style={{
                backgroundColor: "rgba(0, 155, 206, 0.9)",
                color: "#fff",
              }}
            >
              <div
                className="msg-header-left"
                style={{ gap: "1rem", visibility: "visible" }}
              >
                <button
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0.5rem",
                  }}
                  onClick={() => setSelectedMessages([])}
                >
                  <ArrowLeft color="#fff" />
                </button>
                <span style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
                  {selectedMessages.length}
                </span>
              </div>
              <div
                className="msg-header-right"
                style={{ gap: "1rem", paddingRight: "1rem" }}
              >
                {selectedMessages.length === 1 &&
                  selectedMessages[0].sender_id === String(profile?.id) &&
                  !selectedMessages[0].is_deleted_for_everyone && (
                    <button
                      className="msg-icon-btn"
                      onClick={handlePrepareEdit}
                      title="Edit"
                    >
                      <Edit2 color="#fff" size={20} />
                    </button>
                  )}
                {canCopyMessages && (
                  <button
                    className="msg-icon-btn"
                    onClick={handleCopyMessages}
                    title="Copy"
                  >
                    <Copy color="#fff" size={20} />
                  </button>
                )}
                <button
                  className="msg-icon-btn"
                  onClick={handleDeleteSelected}
                  title="Delete"
                >
                  <Trash2 color="#fff" size={20} />
                </button>
              </div>
            </div>
          ) : (
            <div className="msg-chat-header">
              <div
                className="msg-header-left"
                style={{
                  visibility: hasChatPartners ? "visible" : "hidden",
                  alignItems: "flex-start",
                  gap: "2rem",
                }}
              >
                {/* Partner Group */}
                <div className="msg-team-group">
                  <div className="msg-team-avatars">
                    <div
                      className="msg-header-avatar"
                      style={{
                        zIndex: 2,
                        cursor:
                          activeChat?.id === "admin_social_paws"
                            ? "default"
                            : "pointer",
                      }}
                      onClick={(e) => handleAvatarClick(e, activeChat, "user")}
                    >
                      <img
                        src={activeChat?.avatar || userPlaceholder}
                        alt="Partner"
                        onError={handleImgError}
                        style={{
                          padding:
                            activeChat?.id === "admin_social_paws"
                              ? "5px"
                              : "0",
                          backgroundColor:
                            activeChat?.id === "admin_social_paws"
                              ? "#e3f2fd"
                              : "transparent",
                        }}
                      />
                    </div>
                    {activeChat?.userDogStatus === 1 && (
                      <div
                        className="msg-header-avatar overlap"
                        style={{ zIndex: 1, cursor: "pointer" }}
                        onClick={(e) => handleAvatarClick(e, activeChat, "dog")}
                      >
                        <img
                          src={activeChat?.partnerDogImage || dogPlaceholder}
                          alt="Partner Dog"
                          onError={handleImgError}
                        />
                      </div>
                    )}
                  </div>
                  <div className="msg-team-names">
                    <div className="msg-name-item">
                      <Heart
                        size={18}
                        color="#727272"
                        strokeWidth={2.5}
                        className="msg-heart-icon"
                      />
                      <span className="msg-header-user-name">
                        {activeChat?.name}
                      </span>
                    </div>
                    {activeChat?.userDogStatus === 1 && (
                      <div className="msg-name-item">
                        <Bone
                          size={18}
                          color="#000"
                          strokeWidth={2.5}
                          className="msg-bone-icon"
                        />
                        <span className="msg-header-user-name">
                          {activeChat?.partnerDogName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Logged-in User Group */}
                <div className="msg-team-group">
                  <div className="msg-team-avatars">
                    <div className="msg-header-avatar" style={{ zIndex: 2 }}>
                      <img
                        src={myUserImageRaw || userPlaceholder}
                        alt="Me"
                        onError={handleImgError}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          navigate(`/user-profile`, {
                            state: { activeView: "user" },
                          });
                        }}
                      />
                    </div>
                    {userDogStatus === 1 && (
                      <div
                        className="msg-header-avatar overlap"
                        style={{ zIndex: 1 }}
                      >
                        <img
                          src={myDogImageRaw || dogPlaceholder}
                          alt="My Dog"
                          onError={handleImgError}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            navigate(`/user-profile`, {
                              state: { activeView: "dog" },
                            });
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="msg-team-names">
                    <div className="msg-name-item">
                      <Heart
                        size={18}
                        color="#727272"
                        strokeWidth={2.5}
                        className="msg-heart-icon"
                      />
                      <span className="msg-header-user-name">{myName}</span>
                    </div>
                    {userDogStatus === 1 && (
                      <div className="msg-name-item">
                        <Bone
                          size={18}
                          color="#000"
                          strokeWidth={2.5}
                          className="msg-bone-icon"
                        />
                        <span className="msg-header-user-name">
                          {myDogName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {activeChat &&
                activeChat.id !== "admin_social_paws" &&
                (activeChat.walkDate ||
                  (activeChat.availability &&
                    activeChat.availability.length > 0)) && (
                  <div className="msg-header-right desktop-only">
                    {/* Only shown for pup_parent (status 1) */}
                    {userRole === "pup_parent" && (
                      <div
                        className="msg-header-action"
                        onClick={handleUnifiedAvailability}
                        style={{
                          opacity: isCheckingAvailability ? 0.6 : 1,
                          pointerEvents: isCheckingAvailability
                            ? "none"
                            : "auto",
                        }}
                      >
                        {isCheckingAvailability ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Calendar size={18} />
                        )}
                        <span>
                          {myDogName}'s
                          <br />
                          Availability
                        </span>
                      </div>
                    )}
                    {/* Only shown for pup_pals (status 0) */}
                    {userRole === "pup_pals" && (
                      <>
                        <div
                          className="msg-header-action"
                          onClick={handleGoToAvailabilityPage2}
                        >
                          <Calendar size={18} />
                          <span>get Availability</span>
                        </div>
                      </>
                    )}
                    {/* Shown for BOTH pup_parent (1) and pup_pals (0) */}
                    <div
                      className="msg-header-action"
                      onClick={handleGoToPupCarePage}
                    >
                      <FileText size={18} />
                      <span>
                        PupCare
                        <br />
                        Playbook
                      </span>
                    </div>

                    <div
                      className="msg-header-action"
                      onClick={handleGoToCurrentBookingsPage}
                    >
                      <CalendarCheck size={18} />
                      <span>
                        Current
                        <br />
                        Bookings
                      </span>
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* DYNAMIC MEETING BANNER */}
          {activeChat &&
            activeChat.id !== "admin_social_paws" &&
            (activeChat.walkDate ||
              (activeChat.availability &&
                activeChat.availability.length > 0)) && (
              <div className="msg-meeting-banner">
                <p>
                  You are all meeting for your walk together
                  {activeChat.availability &&
                  activeChat.availability.length > 0 ? (
                    <>
                      {" "}
                      with{" "}
                      <span className="msg-highlight-text">
                        {activeChat.availability.join(", ")}
                      </span>{" "}
                    </>
                  ) : (
                    <>
                      {" "}
                      on{" "}
                      <span className="msg-highlight-text">
                        {activeChat.walkDate
                          ? format(
                              activeChat.walkDate,
                              "eee, MMM d 'at' h:mm a",
                            )
                          : "TBD"}
                      </span>
                    </>
                  )}{" "}
                  at{" "}
                  <span className="msg-highlight-text">{meetingAddress}</span>
                </p>
              </div>
            )}

          {/* MESSAGES FEED */}
          <div
            className="msg-feed"
            ref={messagesEndRef}
            onClick={() => {
              if (selectedMessages.length > 0) setSelectedMessages([]);
            }}
          >
            {!loadingMessages && (!allMessages || allMessages.length === 0) && (
              <div className="msg-empty-state">
                <h3>No messages yet</h3>
                <p>
                  {activeChat
                    ? `Send a message to start your conversation with ${activeChat.name}!`
                    : "No active conversations selected."}
                </p>
              </div>
            )}

            {groupedMessages?.map((group, groupIndex) => (
              <div
                key={group.dateStr || groupIndex}
                className="msg-date-group"
                style={{ display: "flex", flexDirection: "column" }}
              >
                {/* DATE DIVIDER */}
                {group.dateStr && (
                  <div
                    className={`msg-date-divider ${
                      group.dateStr === "Today"
                        ? "static-badge"
                        : "floating-badge"
                    }`}
                  >
                    <span>{group.dateStr}</span>
                  </div>
                )}

                {group.messages.map((msg, index) => {
                  const isMe = msg.sender_id === `${profile.id}`;
                  const senderAvatar = isMe
                    ? displayAvatar
                    : activeChat?.displayAvatar;
                  const isSelected = selectedMessages.some(
                    (m) => m.id === msg.id,
                  );
                  const isDeletedForEveryone = msg.is_deleted_for_everyone;
                  const isUploading = msg.is_uploading;

                  return (
                    <div
                      key={msg.id || index}
                      className={`msg-row-wrapper ${isMe ? "me" : "them"} ${isSelected ? "selected" : ""}`}
                      onPointerDown={(e) => handlePointerDown(e, msg)}
                      onPointerUp={clearLongPressTimer}
                      onPointerLeave={clearLongPressTimer}
                      onPointerCancel={clearLongPressTimer}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (msg.sender_id === "admin_social_paws") return; // Block selecting Admin broadcasts
                        if (isLongPressRef.current) {
                          isLongPressRef.current = false;
                          return;
                        }

                        if (selectedMessages.length > 0) {
                          toggleMessageSelection(msg);
                        }
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        if (msg.sender_id === "admin_social_paws") return; // Block selecting Admin broadcasts
                        if (e.button === 2 && !isSelected)
                          toggleMessageSelection(msg);
                      }}
                    >
                      <div className={`msg-row ${isMe ? "me" : "them"}`}>
                        {!isMe && (
                          <div className="msg-avatar-side them">
                            <img
                              src={senderAvatar || userPlaceholder}
                              alt="Partner"
                              onError={handleImgError}
                              style={{
                                padding:
                                  activeChat?.id === "admin_social_paws"
                                    ? "2px"
                                    : "0",
                              }}
                            />
                          </div>
                        )}

                        {/* MESSAGE BUBBLE & TIME WRAPPER */}
                        <div
                          className={`msg-content-wrapper-inner ${isMe ? "me" : "them"}`}
                          style={{ opacity: isUploading ? 0.7 : 1 }}
                        >
                          <div
                            className={`msg-bubble ${isMe ? "me" : "them"} ${isDeletedForEveryone ? "deleted" : ""}`}
                          >
                            {isDeletedForEveryone ? (
                              <div className="msg-deleted-text">
                                <Ban
                                  size={14}
                                  style={{ marginRight: "6px", opacity: 0.5 }}
                                />
                                <i>
                                  {isMe
                                    ? "You deleted this message"
                                    : "This message was deleted"}
                                </i>
                              </div>
                            ) : (
                              <>
                                {/* IMAGES */}
                                {msg.file && msg.file.isImage && (
                                  <div
                                    style={{
                                      position: "relative",
                                      display: "inline-block",
                                    }}
                                  >
                                    <img
                                      src={msg.file.url}
                                      alt={msg.file.name}
                                      className="msg-chat-image"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (
                                          selectedMessages.length === 0 &&
                                          !isUploading
                                        )
                                          setPreviewMedia({
                                            url: msg.file.url,
                                            isVideo: false,
                                          });
                                      }}
                                      style={{
                                        display: "block",
                                        maxWidth: "240px",
                                        maxHeight: "350px",
                                        objectFit: "cover",
                                        borderRadius: "10px",
                                      }}
                                    />
                                    {isUploading && (
                                      <div
                                        style={{
                                          position: "absolute",
                                          inset: 0,
                                          display: "flex",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          backgroundColor: "rgba(0,0,0,0.4)",
                                          borderRadius: "10px",
                                        }}
                                      >
                                        <Loader2
                                          className="animate-spin"
                                          color="#fff"
                                          size={30}
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* VIDEOS */}
                                {msg.file && msg.file.isVideo && (
                                  <div
                                    className="msg-chat-video-wrapper"
                                    style={{
                                      position: "relative",
                                      display: "inline-block",
                                    }}
                                  >
                                    <video
                                      src={`${msg.file.url}#t=0.001`}
                                      controls={!isUploading}
                                      preload="metadata"
                                      className="msg-chat-video"
                                      style={{
                                        maxWidth: "240px",
                                        maxHeight: "350px",
                                        width: "100%",
                                        objectFit: "contain",
                                        borderRadius: "10px",
                                        display: "block",
                                        backgroundColor: "#000",
                                      }}
                                    />
                                    {isUploading && (
                                      <div
                                        style={{
                                          position: "absolute",
                                          inset: 0,
                                          display: "flex",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          backgroundColor: "rgba(0,0,0,0.4)",
                                          borderRadius: "10px",
                                        }}
                                      >
                                        <Loader2
                                          className="animate-spin"
                                          color="#fff"
                                          size={30}
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* OTHER DOCUMENTS */}
                                {msg.file &&
                                  !msg.file.isImage &&
                                  !msg.file.isVideo && (
                                    <a
                                      href={isUploading ? "#" : msg.file.url}
                                      download={
                                        isUploading ? false : msg.file.name
                                      }
                                      target="_blank"
                                      rel="noreferrer"
                                      className="msg-file-download"
                                      onClick={(e) => {
                                        if (
                                          selectedMessages.length > 0 ||
                                          isUploading
                                        )
                                          e.preventDefault();
                                      }}
                                    >
                                      <Paperclip size={16} />
                                      {msg.file.name}
                                      {isUploading && (
                                        <Loader2
                                          className="animate-spin"
                                          size={14}
                                          style={{ marginLeft: "6px" }}
                                        />
                                      )}
                                    </a>
                                  )}

                                {/* TEXT CONTENT */}
                                {msg.message && (
                                  <div
                                    style={{
                                      marginTop: msg.file ? "10px" : "0",
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {renderMessageWithLinks(msg.message)}
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          <div
                            className="msg-meta"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            {msg.is_edited && !isDeletedForEveryone && (
                              <span className="msg-edited-label">Edited</span>
                            )}
                            <span className="msg-time">
                              {msg.computedDate.toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>

                            {isMe &&
                              (msg.status === "sending" ||
                                msg.status === "uploading") && (
                                <Clock size={12} color="#777" />
                              )}
                            {isMe && msg.status === "failed" && (
                              <AlertCircle
                                size={14}
                                color="#e53935"
                                title="Message failed to send"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {hasChatPartners && activeChat?.id !== "admin_social_paws" && (
            <div className="msg-action-buttons-row mobile-only">
              <div className="msg-header-right">
                {/* Only shown for pup_parent (status 1) */}
                {userRole === "pup_parent" && (
                  <div
                    className="msg-header-action"
                    onClick={handleUnifiedAvailability}
                    style={{
                      opacity: isCheckingAvailability ? 0.6 : 1,
                      pointerEvents: isCheckingAvailability ? "none" : "auto",
                    }}
                  >
                    {isCheckingAvailability ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Calendar size={18} />
                    )}
                    <span>
                      {myDogName}'s
                      <br />
                      Availability
                    </span>
                  </div>
                )}
                {/* Only shown for pup_pals (status 0) */}
                {userRole === "pup_pals" && (
                  <>
                    <div
                      className="msg-header-action"
                      onClick={handleGoToAvailabilityPage2}
                    >
                      <Calendar size={18} />
                      <span>get Availability</span>
                    </div>
                  </>
                )}
                {/* Shown for BOTH pup_parent (1) and pup_pals (0) */}
                <div
                  className="msg-header-action"
                  onClick={handleGoToPupCarePage}
                >
                  <FileText size={18} />
                  <span>
                    PupCare
                    <br />
                    Playbook
                  </span>
                </div>

                <div
                  className="msg-header-action"
                  onClick={handleGoToCurrentBookingsPage}
                >
                  <CalendarCheck size={18} />
                  <span>
                    Current
                    <br />
                    Bookings
                  </span>
                </div>
              </div>

              <div className="msg-buttons-row">
                <button
                  className="msg-action-btn toss-treat"
                  disabled={isTossingTreat}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleTossTreat(activeChat.dog_id);
                  }}
                >
                  {isTossingTreat ? (
                    <Loader2 className="animate-spin" size={12} />
                  ) : (
                    "TOSS A TREAT"
                  )}
                </button>

                <button
                  className="msg-action-btn book-walk"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleBookWalk();
                  }}
                >
                  OFFER A WALK
                </button>
              </div>
            </div>
          )}

          {hasChatPartners && activeChat?.id !== "admin_social_paws" && (
            <div className="msg-action-buttons-row desktop-only">
              <button
                className="msg-action-btn toss-treat"
                disabled={isTossingTreat}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleTossTreat(activeChat.dog_id);
                }}
              >
                {isTossingTreat ? (
                  <Loader2 className="animate-spin" size={12} />
                ) : (
                  "TOSS A TREAT"
                )}
              </button>

              <button
                className="msg-action-btn book-walk"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleBookWalk();
                }}
              >
                OFFER A WALK
              </button>
            </div>
          )}

          {/* INPUT AREA */}
          {activeChat?.id === "admin_social_paws" ? (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                backgroundColor: "#f5fcfd",
                borderTop: "1px solid rgba(0,0,0,0.08)",
                color: "#005b7d",
                fontWeight: "600",
              }}
            >
              Replies are disabled for official Social Paws announcements.
            </div>
          ) : (
            <div className="msg-input-wrapper">
              {editingMessageId && (
                <div className="msg-editing-banner">
                  <span>Editing message</span>
                  <button
                    onClick={() => {
                      setEditingMessageId(null);
                      setInputText("");
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              {/* Selected File Preview */}
              {selectedFile && (
                <div className="msg-selected-file-preview">
                  {selectedFile.isImage || selectedFile.isVideo ? (
                    <div className="msg-selected-image-wrapper">
                      {selectedFile.isImage ? (
                        <img
                          src={selectedFile.previewUrl}
                          alt="preview"
                          className="msg-selected-image"
                          style={{
                            maxWidth: "80px",
                            maxHeight: "80px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                        />
                      ) : (
                        <video
                          src={`${selectedFile.previewUrl}#t=0.001`}
                          className="msg-selected-video"
                          preload="metadata"
                          style={{
                            maxWidth: "80px",
                            maxHeight: "80px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                        />
                      )}
                      <button
                        className="msg-remove-selected-file"
                        onClick={() => {
                          URL.revokeObjectURL(selectedFile.previewUrl);
                          setSelectedFile(null);
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="msg-file-chip">
                      <Paperclip size={16} />
                      <span>{selectedFile.name}</span>
                      <button
                        className="msg-remove-selected-file"
                        onClick={() => setSelectedFile(null)}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div
                className="msg-input-area"
                style={{ border: selectedFile ? "none" : "" }}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                  accept="image/*, video/*, .pdf, .doc, .docx, .xls, .xlsx, .txt"
                />

                <button
                  className="msg-attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSending}
                >
                  <Paperclip size={20} color="#555" />
                </button>

                <textarea
                  ref={textareaRef}
                  placeholder="Message"
                  className="msg-text-input"
                  value={inputText}
                  onKeyDown={(e) => {
                    if (
                      !isMobile &&
                      e.key === "Enter" &&
                      !e.shiftKey &&
                      !e.nativeEvent.isComposing &&
                      e.nativeEvent.keyCode !== 229
                    ) {
                      e.preventDefault();
                      if (!e.repeat) handleSend();
                    }
                  }}
                  onChange={(e) => {
                    setInputText(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 96)}px`;
                  }}
                  disabled={isSending}
                  rows={1}
                  style={{
                    resize: "none",
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }}
                />

                <button
                  className="msg-send-btn"
                  onClick={handleSend}
                  disabled={isSending}
                  style={{
                    cursor: isSending ? "default" : "pointer",
                    opacity: isSending ? 0.6 : 1,
                  }}
                >
                  {isSending ? (
                    <Loader2
                      size={20}
                      className="animate-spin"
                      color="rgba(0, 155, 206, 1)"
                    />
                  ) : (
                    <SendHorizontal size={20} color="rgba(0, 155, 206, 1)" />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DELETE MODAL */}
      {showDeleteModal && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="delete-modal-card">
            <h3 style={{ marginBottom: "1.5rem" }}>Delete message?</h3>
            <div className="delete-modal-actions">
              {canDeleteForEveryone && (
                <button
                  className="delete-btn outline"
                  onClick={() => executeDelete("everyone")}
                  disabled={deleteAllLoading}
                >
                  {deleteAllLoading ? "Deleting..." : "DELETE FOR EVERYONE"}
                </button>
              )}
              <button
                className="delete-btn outline"
                onClick={() => executeDelete("me")}
                disabled={deleteMeLoading}
              >
                {deleteMeLoading ? "Deleting..." : "DELETE FOR ME"}
              </button>
              <button
                className="delete-btn text"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteMeLoading || deleteAllLoading}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* INCOMPLETE PROFILE MODAL */}
      {showIncompleteProfileModal && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div
            className="book-modal-card"
            style={{
              padding: "1.5rem",
              textAlign: "center",
              width: window.innerWidth >= 1024 ? "400px" : "",
              height: window.innerWidth >= 1024 ? "180px" : "",
            }}
          >
            <button
              className="modal-close-btn"
              onClick={() => setShowIncompleteProfileModal(false)}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>

            <h2
              className="book-title"
              style={{
                marginTop: "1rem",
                marginBottom: "1rem",
                fontSize: "1rem",
              }}
            >
              Want to interact with the community? Complete your profile first!
              🐾
            </h2>

            <div
              style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
            >
              <button
                className="profile-btn-half"
                onClick={() => {
                  setShowIncompleteProfileModal(false);
                  rememberCompletionReturn(profile?.id, { pId: activeChat?.id, activeView: "dog" });
                  navigate("/team-selection", {
                    state: { routePath: "checkCompletionStatus" },
                  });
                }}
              >
                CONTINUE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TREAT MODAL */}
      {showTreatModal && treatDog && (
        <div className="treat-modal-overlay">
          <div className="treat-modal-card">
            <button
              className="treat-close-btn"
              onClick={handleCloseModal}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>
            <div className="treat-modal-content">
              <div className="treat-icons-row">
                <div className="pro-treat-dog-avatar">
                  <img
                    src={treatDog?.displayAvatar || dogPlaceholder}
                    alt={treatDog.displayName}
                    onError={handleImgError}
                  />
                </div>
                <img
                  src={tablerBone}
                  alt={"tablerBoneIcon"}
                  style={{ width: "60px" }}
                />
              </div>
              <h2 className="treat-text-main">
                You just tossed {treatDog?.displayName} a treat!
              </h2>
              <p className="treat-text-sub">
                Treats are limited to one per day per dog
              </p>
              <h3 className="treat-high-paw">HIGH PAW!</h3>
            </div>
          </div>
        </div>
      )}

      {/* OFFER A WALK MODAL */}
      {showBookWalkModal && treatDog && (
        <div className="modal-overlay" style={{ zIndex: "9999" }}>
          <div className="book-modal-card">
            <button
              className="modal-close-btn"
              onClick={handleCloseModal}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>

            <div className="book-modal-header-icons">
              <div className="pro-treat-dog-avatar">
                <img
                  src={treatDog.displayAvatar || userPlaceholder}
                  alt={treatDog.displayName}
                  onError={handleImgError}
                />
              </div>
            </div>

            <h2 className="book-title">When would you like to walk?</h2>
            <p className="book-subtitle">
              <b style={{ color: "#000" }}>Take the first step! 🐾</b>
              <br />
              Invite {getFirstName(
                treatDog?.user_name || treatDog?.dog_name,
              )}{" "}
              for a walk. Suggest a time or keep it flexible, you can work out
              the details together once they accept.
            </p>

            <div className="book-tabs">
              <span
                className={`book-tab ${walkTab === "calendar" ? "active" : ""}`}
                onClick={() => setWalkTab("calendar")}
              >
                Calendar
              </span>
              <span
                className={`book-tab ${walkTab === "flexible" ? "active" : ""}`}
                onClick={() => setWalkTab("flexible")}
              >
                I'm Flexible
              </span>
            </div>

            {walkTab === "calendar" && (
              <div className="tab-content-calendar">
                <div className="calendar-layout">
                  <div className="custom-calendar-container">
                    <div className="cal-header-info"></div>
                    <div className="cal-selected-display">
                      <h2>
                        {value ? format(value, "EEE, MMM d") : "Select a date"}
                      </h2>
                    </div>

                    <div className="rdp-wrapper-purple">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box
                          sx={{ width: "100%", maxWidth: 360 }}
                          onClick={handleCalendarClick}
                        >
                          <DateCalendar
                            value={value}
                            disablePast
                            onChange={(newValue) => {
                              setValue(newValue);
                              setSelectedTime(null);
                              handleScrollToTime();
                            }}
                          />
                        </Box>
                      </LocalizationProvider>
                    </div>
                  </div>

                  <div className="time-selector-area" ref={timeDropdownRef}>
                    <label>TIME</label>

                    <div
                      className="custom-dropdown"
                      style={{
                        backgroundColor: selectedTime
                          ? "#fff"
                          : "rgba(182, 252, 238, 1)",
                      }}
                      onClick={() => setShowTimeDropdown((p) => !p)}
                    >
                      <span>{selectedTime || "Choose a time"}</span>
                      <ChevronDown size={16} />
                    </div>

                    {showTimeDropdown && (
                      <div className="time-dropdown-list">
                        {TIME_SLOTS.map((time) => (
                          <div
                            key={time}
                            className={`time-option ${
                              selectedTime === time ? "active" : ""
                            }`}
                            onClick={() => {
                              setSelectedTime(time);
                              setShowTimeDropdown(false);
                            }}
                          >
                            {time}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="book-modal-next-container">
                      <button
                        className={`book-modal-next-btn ${!selectedTime ? "disabled" : ""}`}
                        disabled={!selectedTime}
                        onClick={() => {
                          const [time, meridian] = selectedTime.split(" ");
                          const [hour, minute] = time.split(":").map(Number);
                          const finalDateTime = value
                            .hour(
                              meridian === "PM" && hour !== 12
                                ? hour + 12
                                : hour === 12 && meridian === "AM"
                                  ? 0
                                  : hour,
                            )
                            .minute(minute)
                            .second(0);
                          setSelectedDate(finalDateTime);
                          openMeetingPoint();
                        }}
                      >
                        NEXT
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {walkTab === "flexible" && (
              <div className="tab-content-flexible">
                <div className="flexible-grid">
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("mornings")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("mornings")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={morningIcon}
                      alt="Mornings"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Mornings</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("afternoons")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("afternoons")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={afternoonIcon}
                      alt="Afternoon"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Afternoons</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("evenings")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("evenings")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={eveningIcon}
                      alt="Evening"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Evenings</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("weekdays")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("weekdays")}
                  >
                    <img
                      src={weekedaysIcon}
                      alt="Weekdays"
                      style={{
                        height: "40px",
                        width: "35px",
                        marginTop: "10px",
                      }}
                    />
                    <span>Weekdays</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("weekends")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("weekends")}
                    style={{ gap: "15px" }}
                  >
                    <img
                      src={weekenedsIcon}
                      alt="Weekends"
                      style={{
                        height: "35px",
                        width: "35px",
                        marginTop: "10px",
                      }}
                    />
                    <span>Weekends</span>
                  </div>
                  <div
                    className={`flex-option ${
                      selectedFlexibleOptions.includes("vacation")
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleFlexibleOption("vacation")}
                    style={{ gap: "0" }}
                  >
                    <img
                      src={vacationIcon}
                      alt="vacations"
                      style={{ height: "60px", width: "60px" }}
                    />
                    <span>Vacations</span>
                  </div>
                </div>
                <div className="book-modal-submit-container">
                  <button
                    className={`book-modal-submit-btn ${selectedFlexibleOptions.length === 0 ? "disabled" : ""}`}
                    disabled={selectedFlexibleOptions.length === 0}
                    onClick={handleFlexibleSubmit}
                  >
                    SUBMIT
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MEETING POINT MODAL */}
      {showMeetingPointModal && (
        <div className="meeting-point-overlay">
          <div className="meeting-point-card">
            <div className="meeting-point-header">
              <button className="mp-back-btn" onClick={backToBookWalk}>
                <ArrowLeft size={22} color="#000" strokeWidth={3} />
                <span style={{ color: "#000", fontWeight: "700" }}>Back</span>
              </button>
              <button
                className="mp-close-btn"
                onClick={closeAllModalsFully}
                style={{ zIndex: 999 }}
              >
                <X size={16} color="rgba(166, 28, 31, 1)" strokeWidth={4} />
              </button>
            </div>

            <div className="meeting-point-content">
              <h2 className="mp-title">SELECT MEETING LOCATION</h2>
              <p className="mp-subtitle">
                Choose from common locations or tap on map to pin a location on
                map
              </p>

              <MeetingLocationPicker
                profile={profile}
                partnerId={activeChat?.id}
                dogId={activeChat?.userDogStatus === 0 ? profile?.dogs?.[0]?.id : activeChat?.dogId}
                isMapLoaded={isLoaded}
                initialCenter={userLocation}
                selectedLocation={selectedMeetingLocation}
                selectedAddress={selectedMeetingAddress}
                onSelect={(place) => {
                  setSelectedMeetingLocation(place);
                  setSelectedMeetingAddress(place.address || place.name);
                }}
              />

              <button className="mp-confirm-btn" disabled={!hasMeetingLocation(selectedMeetingAddress)} onClick={openWalkConfirm}>
                NEXT: PERSONALIZE YOUR OFFER
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WALK CONFIRM MODAL (Message) */}
      {showWalkConfirmModal && (
        <div className="walk-confirm-overlay">
          <div className="walk-confirm-card">
            <div className="walk-confirm-header">
              <button className="wc-back-btn" onClick={backToMeetingPoint}>
                <ArrowLeft size={22} color="#000" strokeWidth={3} />
                <span style={{ color: "#000", fontWeight: "700" }}>Back</span>
              </button>
              <button
                className="wc-close-btn"
                onClick={closeAllModalsFully}
                style={{ zIndex: 999 }}
              >
                <X size={16} color="rgba(166, 28, 31, 1)" strokeWidth={4} />
              </button>
            </div>
            <div className="walk-confirm-content">
              <h2 className="wc-title">
                You would like to walk with {treatDog?.displayName}{" "}
                {selectedDate ? (
                  `on ${dayjs(selectedDate).format("ddd, MMM D [at] h:mma")}`
                ) : (
                  <>
                    with <span>{selectedFlexibleOptions.join(", ")}</span>{" "}
                  </>
                )}
              </h2>

              <p className="wc-message-label">
                Personalize your offer with a message
              </p>
              <textarea
                className="wc-message-input"
                placeholder="Write here"
                value={walkMessage}
                onChange={(e) => setWalkMessage(e.target.value)}
              />

              <div className="wc-actions-row">
                <button
                  className="wc-btn confirm"
                  onClick={() => {
                    setShowWalkConfirmModal(false);
                    setShowSuccessModal(true);
                  }}
                >
                  NEXT: CONFIRM YOUR OFFER BEFORE SENDING
                </button>
              </div>
              <p className="wc-footer-text">
                We will reach out to {treatDog?.user_name} with your suggested
                time and find the best time for your first group walk to either
                confirm or suggest another time or place, check your bookings
                tab to see more!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal-card">
            <button
              className="modal-close-btn"
              onClick={closeAllModalsFully}
              style={{
                zIndex: 999,
                position: "absolute",
                top: "15px",
                right: "15px",
              }}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>
            <h2 className="success-title">
              Your request to{" "}
              {getFirstName(treatDog?.user_name || treatDog?.dog_name)} is sent
            </h2>

            {walkMessage && (
              <div className="success-msg-preview">
                <p style={{ margin: "0 0 4px 0", fontWeight: "600" }}>
                  Your message:
                </p>
                <p style={{ margin: 0, fontStyle: "italic" }}>
                  "{walkMessage}"
                </p>
              </div>
            )}

            <h3 className="success-mp-title">Your Meeting point will be:</h3>
            <div className="success-map-box">
              <div className="success-map-placeholder">
                <div className="group-walk-pill">
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: "#448a7b",
                      }}
                    ></div>{" "}
                    GROUP WALK
                  </span>
                </div>
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={selectedMeetingLocation || userLocation}
                    zoom={15}
                    options={{
                      disableDefaultUI: true,
                      gestureHandling: "none",
                    }}
                  >
                    <Marker
                      position={selectedMeetingLocation || userLocation}
                    />
                  </GoogleMap>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "#e3f2fd",
                    }}
                  />
                )}
              </div>
              <div className="success-mp-details">
                <h4>
                  {selectedMeetingAddress
                    ? selectedMeetingAddress.split(",")[0]
                    : "Selected Location"}
                </h4>
                <p>
                  <MapPin size={14} color="#009bce" /> {selectedMeetingAddress}
                </p>
              </div>

              <div className="success-chips">
                <div className="success-chip blue">
                  <span>{getDynamicDateLabel(selectedDate)}</span>
                  <strong style={{ color: "#000" }}>
                    {selectedDate
                      ? dayjs(selectedDate).format("ddd, MMM D")
                      : selectedFlexibleOptions.join(", ")}
                  </strong>
                </div>
                {selectedDate && (
                  <div className="success-chip green">
                    <span>{getDynamicTimeLabel(selectedDate)}</span>
                    <strong style={{ color: "#000" }}>
                      {dayjs(selectedDate).format("h:mm A")}
                    </strong>
                  </div>
                )}
              </div>
            </div>
            <button
              className="success-done-btn"
              onClick={handleCreateWalk}
              disabled={isCreatingWalk}
            >
              {isCreatingWalk ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Loader2 className="animate-spin" size={18} />
                  <span>DONE</span>
                </div>
              ) : (
                "DONE"
              )}
            </button>
          </div>
        </div>
      )}

      {/* ENLARGED MEDIA PREVIEW MODAL */}
      {previewMedia && (
        <div
          className="msg-image-preview-overlay"
          onClick={() => setPreviewMedia(null)}
        >
          <div
            className="msg-image-preview-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="msg-image-preview-close"
              onClick={() => setPreviewMedia(null)}
            >
              <X size={20} />
            </button>

            {previewMedia.isVideo ? (
              <video
                src={`${previewMedia.url}#t=0.001`}
                controls
                autoPlay
                preload="metadata"
                className="msg-image-preview-full"
                style={{ maxHeight: "80vh", maxWidth: "100%" }}
              />
            ) : (
              <img
                src={previewMedia.url}
                alt="Preview"
                className="msg-image-preview-full"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
